// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseInputSearch, BaseMoreAction, BaseTable, BaseToolbar, ConfirmModal, Loading } from '~/components/common';
// Others
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  EMPTY_STRING,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IVenue } from '~/utils/interface/venue';
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { venueActions } from '~/thunks/venue/venueSlice';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { VENUE_PERMISSIONS } from '~/utils/constants/permissions';
import { IPaginationResponse } from '~/utils/interface/base';
import { deleteVenue, getListVenue } from '~/thunks/venue/venueThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './Venue.module.scss';
import { icons } from '~/assets';
import { formatPhoneNumber } from '~/utils/helper/common';

type Props = {};

const cx = classNames.bind(styles);

const columnsVenue = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IVenue>[] => {
  return [
    {
      id: 'name',
      label: t('table_header_title_name'),
      render: (record: IVenue) => <Box className={cx('textValue')}>{record?.name || EMPTY_STRING}</Box>,
    },
    {
      id: 'contactName',
      label: t('table_header_title_contact_name'),
      render: (record: IVenue) => <Box className={cx('textValue')}>{record?.contactName || EMPTY_STRING}</Box>,
    },
    {
      id: 'email',
      label: t('table_header_title_email'),
      render: (record: IVenue) => <Box className={cx('textValue')}>{record?.email || EMPTY_STRING}</Box>,
    },
    {
      id: 'phoneNumber',
      label: t('table_header_title_phone'),
      render: (record: IVenue) => (
        <Box className={cx('textValue')}>{formatPhoneNumber(record?.phoneNumber) || EMPTY_STRING}</Box>
      ),
    },
    {
      id: 'website',
      label: t('table_header_title_website'),
      render: (record: IVenue) => (
        <Box
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (record?.website) window.open(record?.website);
          }}
          className={cx('textWebsite')}
        >
          {record?.website || EMPTY_STRING}
        </Box>
      ),
    },
    {
      id: 'address',
      label: t('table_header_title_address'),
      render: (record: IVenue) => <Box className={cx('textValue')}>{record?.address || EMPTY_STRING}</Box>,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IVenue) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id),
                },
              ]
            : []),
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onDelete(record.id),
                  hasOtherColor: true,
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Venue = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateVenue, canViewVenue, canDetailVenue, canEditVenue, canDeleteVenue } =
    usePermissions(VENUE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/venue', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const { isLoading, isRefreshVenueList } = useAppSelector((state) => state.venue);
  //#endregion Selector

  //#region Declare State
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [idVenue, setIdVenue] = useState<string>(EMPTY_STRING);
  const [venueList, setVenueList] = useState<IVenue[]>([]);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListVenue(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshVenueList) return;

    handleGetListVenue(paramObject);

    dispatch(venueActions.setRefreshVenueList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshVenueList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListVenue = (params: IQueryParamBase) => {
    dispatch(getListVenue(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setVenueList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleCloseConfirmDelete = () => {
    setIdVenue(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleAdd = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.createVenue);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createVenue);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.createVenue);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.createVenue);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.createVenue);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.createVenue);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.createVenue);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.createVenue);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.createVenue);
        break;
    }
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateVenue(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateVenue(id));
        break;
    }
  };
  const handleView = (id: string) => {
    if (!id) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailVenue(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailVenue(id));
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdVenue(id);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteVenue(idVenue))
      .unwrap()
      .then((res) => {})
      .catch((_error) => {})
      .finally(() => {
        handleCloseConfirmDelete();
      });
  };
  //#endregion Handle Function

  return (
    <Box id='venuePage' className={cx('container')}>
      <BaseToolbar title={t('venue_title')} isBack description={t('venue_sub_title')}>
        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateVenue && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAdd}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('venue_title_button_add')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewVenue ? venueList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsVenue(t, handleView, handleEdit, handleDelete, canDetailVenue, canEditVenue, canDeleteVenue)}
          onPageChange={handlePageChange}
          {...(canDetailVenue ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default Venue;
